import { type ISupportMessage, type SupportMessageState } from '../interface/support-chat';

// Transcript merge. Every path that adds a message — the optimistic insert, the HTTP
// response, the socket broadcast, a backfill page — goes through `upsertByClientIdThenId`.
//
// Why one shared helper rather than each caller doing it its own way: a sent message
// arrives TWICE, once as the POST response and once as the hub's broadcast to the thread,
// and the broadcast can beat the response. Two code paths each deduping in their own
// style is exactly how the double-bubble bug ships. There is one merge, and it is here.

/**
 * Match key, in priority order.
 *
 * `clientId` first: while a send is in flight the optimistic row has no server id yet, so
 * clientId is the only thing tying the echo back to the placeholder. `id` second, for rows
 * this device never sent (the desk's messages, backfill).
 */
const isSameMessage = (a: ISupportMessage, b: ISupportMessage): boolean => {
	if (a.clientId && b.clientId) return a.clientId === b.clientId;
	if (a.id && b.id) return a.id === b.id;
	return false;
};

/**
 * Delivery states only ever move forward.
 *
 * Out-of-order arrival is normal: a `read` receipt can land before the `delivered` event
 * that logically precedes it, and a backfill page may carry a row stamped `sent` for a
 * message this client already saw read. Taking the later arrival blindly would flick the
 * ticks backwards in front of the user.
 */
const STATE_RANK: Record<SupportMessageState, number> = {
	failed: 0,
	queued: 1,
	sending: 2,
	sent: 3,
	delivered: 4,
	read: 5,
};

/**
 * `failed` is the one state allowed to move backwards — a send that errors after
 * optimistically showing `sending` must be able to say so.
 */
export const mergeState = (current: SupportMessageState, incoming: SupportMessageState): SupportMessageState => {
	if (incoming === 'failed') return 'failed';
	if (current === 'failed') return incoming;
	return STATE_RANK[incoming] >= STATE_RANK[current] ? incoming : current;
};

const mergeOne = (existing: ISupportMessage, incoming: ISupportMessage): ISupportMessage => ({
	...existing,
	...incoming,
	// Never lose the client id: the server echoes it, but a backfill row will not carry it,
	// and dropping it would orphan the placeholder this row is supposed to replace.
	clientId: incoming.clientId ?? existing.clientId,
	id: incoming.id ?? existing.id,
	state: mergeState(existing.state, incoming.state),
});

/** Ascending by sequence — the transcript reads oldest to newest, like a conversation. */
const bySequence = (a: ISupportMessage, b: ISupportMessage) => a.sequence - b.sequence;

/**
 * Insert or update one message, then re-sort.
 *
 * Replaces the matching row in place rather than appending, so the operator's own post
 * does not appear twice once the server's copy arrives.
 */
export const upsertByClientIdThenId = (messages: ISupportMessage[], incoming: ISupportMessage): ISupportMessage[] => {
	const index = messages.findIndex((m) => isSameMessage(m, incoming));
	const next = index === -1 ? [...messages, incoming] : messages.map((m, i) => (i === index ? mergeOne(m, incoming) : m));
	return next.sort(bySequence);
};

/** Fold a batch (a backfill page, a reconnect catch-up) through the same single path. */
export const upsertMany = (messages: ISupportMessage[], incoming: ISupportMessage[]): ISupportMessage[] =>
	incoming.reduce(upsertByClientIdThenId, messages);

/**
 * Highest sequence held, for the `?after=` backfill cursor.
 *
 * Client-only rows are excluded: a queued message has no server sequence, and letting a
 * placeholder set the cursor would ask the server for messages after a sequence it never
 * issued and silently skip the real gap.
 */
export const highestSequence = (messages: ISupportMessage[]): number =>
	messages.reduce((max, m) => (m.id && m.sequence > max ? m.sequence : max), 0);

/**
 * True when the backfill response could not cover the gap.
 *
 * The caller must then discard its tail and refetch rather than stitching. A transcript
 * that looks continuous but silently omits messages is worse, for a dispute record, than
 * an honest hole the UI can label.
 */
export const hasSequenceGap = (messages: ISupportMessage[], expectedFrom: number): boolean => {
	const server = messages.filter((m) => m.id).sort(bySequence);
	if (server.length === 0) return false;
	return server[0].sequence > expectedFrom + 1;
};
